import { useState, memo, useRef } from "react";
import { GeoAltFill, EnvelopeAtFill, Facebook } from 'react-bootstrap-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router';
import { CONTACT_INFO, SOCIAL_LINKS } from '../../constants/config';
import { ROUTES, SECTION_IDS } from '../../constants/routes';
import { prefetchRoute } from '../../utils/prefetchRoute';
import { useContactEmail } from '../../hooks/useContactEmail';
import { checkAndRegisterSend, RateLimitReasons } from '../../utils/contactRateLimiter';
import { logInfo, logError } from '../../utils/errorHandlers';

const initialState = {
    name: "",
    email: "",
    message: "",
    // Honeypot-Feld – sollte von Menschen leer gelassen werden
    company: "",
};

const ContactBlockReasons = {
    NONE: "none",
    HONEYPOT: "honeypot",
    TOO_SOON: "tooSoon",
    TOO_MANY: "tooMany",
};

const isBotSubmission = (companyFieldValue) => Boolean(companyFieldValue && companyFieldValue.trim().length > 0);

const mapBlockReasonToToast = (reason) => {
    switch (reason) {
        case ContactBlockReasons.HONEYPOT:
            toast.info("Nachricht wurde nicht versendet.");
            break;
        case ContactBlockReasons.TOO_SOON:
            toast.warn("Bitte warten Sie einen Moment, bevor Sie eine weitere Nachricht senden.");
            break;
        case ContactBlockReasons.TOO_MANY:
            toast.warn("Sie haben das Formular bereits mehrfach gesendet. Bitte versuchen Sie es später erneut.");
            break;
        default:
            break;
    }
};

const useContactForm = () => {
    const [formData, setFormData] = useState(initialState);
    const { sendContactMessage, isSending } = useContactEmail();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({ ...prevState, [name]: value }));
    };

    const clearState = () => setFormData({ ...initialState });

    const handleSubmit = async (e) => {
        e.preventDefault();

        let blockReason = ContactBlockReasons.NONE;

        if (isBotSubmission(formData.company)) {
            blockReason = ContactBlockReasons.HONEYPOT;
        } else {
            const rateLimitReason = checkAndRegisterSend(Date.now());
            if (rateLimitReason === RateLimitReasons.TOO_SOON) {
                blockReason = ContactBlockReasons.TOO_SOON;
            } else if (rateLimitReason === RateLimitReasons.TOO_MANY) {
                blockReason = ContactBlockReasons.TOO_MANY;
            }
        }

        if (blockReason !== ContactBlockReasons.NONE) {
            if (blockReason === ContactBlockReasons.HONEYPOT) {
                logInfo('Contact', 'honeypot caught potential bot submission');
                clearState();
            }
            mapBlockReasonToToast(blockReason);
            return;
        }

        try {
            await sendContactMessage(e.target);
            toast.success("Ihre Nachricht wurde verschickt");
            clearState();
        } catch (error) {
            logError('Email send error', error);
            toast.error("Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.");
        }
    };

    return { formData, handleChange, handleSubmit, isSubmitting: isSending };
};

const SectionHeader = () => (
    <div className="section-title">
        <h2>Schreibt uns!</h2>
        <p>Füllt einfach das folgende Formular aus und wir melden uns bei Euch.</p>
    </div>
);

const ContactForm = ({ formData, handleChange, handleSubmit, isSubmitting, formRef }) => (
    <form name="sentMessage" onSubmit={handleSubmit} ref={formRef}>
        <div className="row">
            <div className="col-md-6">
                <div className="form-group">
                    <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder="Name"
                        required
                        maxLength={100}
                        value={formData.name}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        aria-label="Name"
                    />
                </div>
            </div>
            <div className="col-md-6">
                <div className="form-group">
                    <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="Email"
                        required
                        maxLength={254}
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        aria-label="Email"
                    />
                </div>
            </div>
        </div>

        {/* Honeypot-Feld, für Screenreader versteckt, visuell offscreen */}
        <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <label htmlFor="company">Firma (bitte leer lassen)</label>
            <input
                type="text"
                id="company"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                value={formData.company}
                onChange={handleChange}
            />
        </div>

        <div className="form-group">
            <textarea
                name="message"
                id="message"
                className="form-control"
                rows="4"
                placeholder="Message"
                required
                maxLength={2000}
                value={formData.message}
                onChange={handleChange}
                disabled={isSubmitting}
                aria-label="Message"
            />
        </div>
        <button
            type="submit"
            className="btn btn-custom btn-lg"
            disabled={isSubmitting}
            aria-label={isSubmitting ? 'Sending message' : 'Send message'}
        >
            {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
    </form>
);

const ContactInfo = ({ email }) => (
    <div className="col-md-3 col-md-offset-1 contact-info">
        <div className="contact-item">
            <h3>Kontaktdaten</h3>
            <span>
                <GeoAltFill /> Adresse
            </span>
            <div className="address">
                <p>
                    <Link to={ROUTES.ADDRESS}
                          onMouseEnter={() => prefetchRoute('address')}>
                        {CONTACT_INFO.address.name}<br />
                        {CONTACT_INFO.address.street}<br />
                        {CONTACT_INFO.address.city}
                    </Link>
                </p>
            </div>
        </div>
        <div className="contact-item">
            <span>
                <EnvelopeAtFill /> E-Mail
            </span>
            <p>{email}</p>
        </div>
    </div>
);

const SocialLinks = ({ facebookUrl }) => (
    <div className="col-md-12">
        <div className="row">
            <div className="social">
                <ul>
                    <li>
                        <a
                            href={facebookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Facebook page"
                        >
                            <Facebook />
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
);

const ContactFooter = () => (
    <div id={SECTION_IDS.FOOTER}>
        <div className="container text-center">
            <p>&copy; 2025 KickerEck Dortmund</p>
        </div>
        <div className="container text-center">
            <p>
                <Link to={ROUTES.IMPRESSUM}>Impressum & Datenschutzerklärung</Link>
            </p>
        </div>
    </div>
);

export const Contact = memo(({ data }) => {
    const formRef = useRef(null);
    const { formData, handleChange, handleSubmit, isSubmitting } = useContactForm();

    return (
        <div>
            <ToastContainer position="top-right" autoClose={3000} />
            <div id={SECTION_IDS.CONTACT}>
                <div className="container">
                    <div className="col-md-8">
                        <div className="row">
                            <SectionHeader />
                            <ContactForm
                                formData={formData}
                                handleChange={handleChange}
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                                formRef={formRef}
                            />
                        </div>
                    </div>
                    <ContactInfo email={data?.email || CONTACT_INFO.generalEmail} />
                    <SocialLinks facebookUrl={data?.facebook || SOCIAL_LINKS.FACEBOOK} />
                </div>
            </div>
            <ContactFooter />
        </div>
    );
});

Contact.displayName = 'Contact';
